export type PurposeType = 'referral' | 'interview' | 'directory' | 'googlemaps' | 'custom';

export interface SafeModeFormData {
  safeModePurpose?: PurposeType;
  area?: string;
  channel?: string;
  service?: string;
  customPurpose?: string;
  // Merge tag fields
  userName?: string;
  companyName?: string;
  industry?: string;
}

// Merge tag defaults - standardized to match ScriptCreator
const MERGE_DEFAULTS = {
  userName: 'Chris',
  companyName: 'Next Level Solutions', 
  industry: 'local business',
  area: 'your area',
  channel: 'a quick recorded interview',
  service: 'referral opportunities',
  customPurpose: 'a quick introduction',
};

function applyMergeTags(text: string, form: SafeModeFormData): string {
  return text
    .replace(/\{\{userName\}\}/g, form.userName || MERGE_DEFAULTS.userName)
    .replace(/\{\{companyName\}\}/g, form.companyName || MERGE_DEFAULTS.companyName)
    .replace(/\{\{industry\}\}/g, form.industry || form.service || MERGE_DEFAULTS.industry)
    .replace(/\{\{area\}\}/g, form.area || MERGE_DEFAULTS.area)
    .replace(/\{\{channel\}\}/g, form.channel || MERGE_DEFAULTS.channel)
    .replace(/\{\{service\}\}/g, form.service || MERGE_DEFAULTS.service)
    .replace(/\{\{customPurpose\}\}/g, form.customPurpose || MERGE_DEFAULTS.customPurpose);
}

const CANONICALS = {
  schedule_confirmed: '"Perfect! I\'ll send a confirmation."',
  consent_email: '"Great! I\'ll email that over."',
  consent_callback_general: '"Perfect! I\'ll try you again later this week."',
  ask_name: '"Sorry, I didn\'t catch your name."',
  ask_name_confirmed: '"Great — and I didn\'t catch your name?"',
  ask_gatekeeper_name: '"Got it — and what\'s their name?"',
  book_now_accept: '"[Day] at [Time] — perfect. I\'ll lock that in."',
  book_now_email: '"What\'s the best email for the invite?"',
  book_now_confirm: '"Great — you\'re set for [Day] at [Time]."',
  rescheduleConfirmed: '"No problem! I\'ll move things around and get back to you."',
  do_not_call: '"Understood! I\'ll make sure we don\'t reach out again."',
  wrong_number: '"My apologies! I\'ll update our records."',
  info_email_requested: '"Absolutely! I\'ll email that information over."',
  gatekeeper_schedule: '"That would be great! I\'ll send the details and we can coordinate."',
  no_action: '"Thanks for your time!"',
  pricing_redirect: '"Fair question — pricing depends on area and volume. The 10-min intro covers that. Does Wed at 2 or Thu at 11 work?"',
  voicemail_standard: '"Hey, it\'s {{userName}} with {{companyName}}. Quick idea to help with {{purpose}} for your {{industry}} team. I\'ll email a 1-pager and try you once more — if you prefer, call or text back at the number on your caller ID. Thanks!"',
};

function s1Opener(purpose: PurposeType, hasCity: boolean): string {
  // CANONICAL S1 - matches Safe Mode exactly, no purpose injection
  return "Hey, this is {{userName}} with {{companyName}} — I'm looking to get some details on your {{industry}} work. Are you usually the person who handles new project inquiries and referrals, or is there someone else on your team?";
}

function s1Instructions(): string {
  return `Agent Instructions (not spoken):
• MANDATORY NAME CAPTURE: If contact confirms they handle it → immediately ask name using ask_name_confirmed canonical
• If gatekeeper says someone else handles it → immediately ask using ask_gatekeeper_name canonical  
• DO NOT offer times until a relevant name is known
• If they answer but don't give a name: Use ask_name canonical once only
• If gatekeeper: Use compliment + [PAUSE] + bridge to gatekeeper flow
• Always get name before proceeding to gatekeeper handling
• If not the decision-maker, follow the Gatekeeper Handling section below before moving forward`;
}

function s2Hook(purpose: PurposeType): string {
  if (purpose === "referral") {
    return `• "We plug you into a pre-ranked site so calls start sooner. Does that sound useful?"
• "Short version: helps {{industry}} businesses get referrals faster. Interested?"
• "Quick way to get included when we send opportunities. Worth a quick chat?"`;
  } else if (purpose === "interview") {
    return `• "It's a site that's already ranking for {{industry}} searches. Quick 10 to see if it fits?"
• "Pre-ranked platform for {{industry}} providers. Tomorrow or the day after work?"
• "Helps {{industry}} businesses get calls faster. Worth a quick look?"`;
  } else if (purpose === "directory") {
    return `• "Simple one-pager with your preferred contact. Worth adding you?"
• "No cost listing for {{industry}} providers. Quick 10 to set up?"
• "Basic profile that helps customers find you. Interested?"`;
  } else if (purpose === "googlemaps") {
    return `• "Quick check to make sure your listing shows up properly. Sound useful?"
• "Helps {{industry}} businesses get found easier. Worth a quick look?"
• "Simple fixes that help customers reach you. Quick 10 to review?"`;
  } else if (purpose === "custom") {
    return `• "{{customPurpose}} — sound useful?"
• "Quick way to help with {{industry}} {{service}}. Worth a chat?"
• "Something that might help your {{industry}} business. Interested?"`;
  }
  return `• "{{customPurpose}} — worth a quick chat?"`;
}

function buildStandardS2ExplainerLogic(): string {
  return `
SHORT EXPLAINER RULE (10-second cap):
If asked "what is it?" → one sentence + pivot to booking

Examples:
• "It's a site that's already ranking for pool-install searches in your area — you plug in and get calls faster."
• "Pre-built platform for {{industry}} providers — helps you get found easier."
• "Simple listing that's already getting traffic — quick way to start getting calls."

Then immediately: "Quick 10 to see if it fits — tomorrow or the day after?"

Agent Instructions (not spoken):
• Keep explainer ≤20 words, one sentence maximum
• Always pivot immediately to scheduling after explainer
• No detailed explanations or features lists
`;
}

function buildHeader(form: SafeModeFormData, scriptMode?: string): string {
  const purpose = form.safeModePurpose || 'referral';
  const headerTitle = scriptMode === 'standard' ? 'Standard Script' : 'Safe Mode v3.1 Call Script';
  return `=== ${headerTitle} ===
Purpose: ${purpose}
Industry: ${form.industry || form.service}
Area: ${form.area}
Channel: ${form.channel}

User: ${form.userName || MERGE_DEFAULTS.userName} from ${form.companyName || MERGE_DEFAULTS.companyName}

`;
}

function buildS1(form: SafeModeFormData): string {
  const purpose = form.safeModePurpose || 'referral';
  const hasCity = form.area && form.area.trim() !== '';
  const opener = applyMergeTags(s1Opener(purpose, hasCity), form);
  
  return `S1 – Rapport / Screening

"${opener}"

${s1Instructions()}

`;
}

function buildGatekeeperHandling(): string {
  return `=== GATEKEEPER HANDLING ===

Goal: Treat the gatekeeper as an ally and create a clear next step, while protecting S2 content for the correct audience.

Rapport First:
• Warm, friendly, and natural
• Show respect for their role using short, natural sentences with space for them to respond
• After name captured, use it once: "Thanks, [Name]."

Examples:
• "You must keep things running smoothly there." [PAUSE]
• "Sounds like you're the one holding everything together." [PAUSE]  
• "I can tell things don't run well without someone steady at the helm." [PAUSE]

Gatekeeper Bridge Line:
• "Got it." [PAUSE] "Would it be possible to set up a quick 10-minute chat with [DM Name]? I'm available [Option 1] or [Option 2]."

Pathfinding Flow:
• If they seem open: Schedule directly with times
• If they cannot schedule: "Would it be alright if I sent over a quick overview by email so they can take a look? That way, I can follow up with a call once they've had a chance to review."
• "What's the best email for them?"
• Optional email spell-back if parse looks off: "Just to confirm, that's doug55@gmail.com — did I get that right?"

Consent & Logging:
• Email given → log consent_email + address, and consent_callback_general
• Callback without email → log consent_callback_general

S2 Protection:
• Do not proceed to S2 unless speaking with the confirmed DM

Fallback Path:
• If no schedule and no email: warm close, log no_action

Tone Guidance:
• Charm > checklist
• Avoid interrogating or demanding DM contact
• Use natural pauses

Transition Examples:
• "Perfect, let me get that set up with them…"
• "Got it — I'll send that over so they can take a look."

`;
}

function buildS2(form: SafeModeFormData, scriptMode?: string): string {
  const purpose = form.safeModePurpose || 'referral';
  const hooks = applyMergeTags(s2Hook(purpose), form);
  
  let s2Content = `S2 – Hook

${hooks}

Agent Instructions (not spoken):
• S2 value line MUST be ≤20 words maximum - one sentence only, then question
• Lead with one hook, then pause for engagement
• Keep tone warm and curious, like you're sharing something useful
• ${purpose === 'googlemaps' ? 'Do NOT immediately go to close' : 'Maintain consultative approach'}

`;

  // Add Standard Mode specific logic
  if (scriptMode === 'standard') {
    s2Content += buildStandardS2ExplainerLogic();
  }

  return s2Content;
}

function buildS3(form: SafeModeFormData): string {
  const purpose = form.safeModePurpose || 'referral';
  if (purpose === "referral") {
    return `S3 – Curiosity Check

• "What types of work do you typically refer out vs. handle in‑house?"
• "How do you usually handle it when you're swamped or have overflow?"
• "Any particular types of projects you'd want to be considered for?"

Agent Instructions (not spoken):
• Freestyle zone - ask at least ONE before any scheduling/close
• Mirror tone and engage naturally
• Cap at ~45s, redirect after 10s off‑topic

`;
  } else if (purpose === "interview") {
    return `S3 – Curiosity Check

• "What's the biggest challenge {{industry}} businesses face right now?"
• "What's working really well for you that others might be missing?"
• "Any trends you're seeing that would be interesting to cover?"

Agent Instructions (not spoken):
• Freestyle zone - ask at least ONE before any scheduling/close
• Mirror tone and engage naturally
• Cap at ~45s, redirect after 10s off‑topic

`;
  } else if (purpose === "directory") {
    return `S3 – Curiosity Check

• "How do new customers usually find you now?"
• "What would you want highlighted in your listing?"
• "Any particular services or specialties you'd want emphasized?"

Agent Instructions (not spoken):
• Freestyle zone - ask at least ONE before any scheduling/close
• Mirror tone and engage naturally
• Cap at ~45s, redirect after 10s off‑topic

`;
  } else if (purpose === "googlemaps") {
    return `S3 – Curiosity Check

• "When was the last time you updated your Google Maps listing?"
• "Do you know if your listing is already claimed under your account?"
• "Have you noticed any issues with how it's showing up in search or on the map?"

Agent Instructions (not spoken):
• Freestyle zone - ask at least ONE before any scheduling/close
• Mirror tone ("Sometimes" → "Yeah, total headache sometimes, right?" / "To be honest" → "Love the real talk.")
• Cap at ~45s, redirect after 10s off‑topic

`;
  } else if (purpose === "custom") {
    return `S3 – Curiosity Check

• "What's working well for you in that area right now?"
• "What would be most helpful for your {{industry}} business?"
• "Any particular challenges you're facing with that?"

Agent Instructions (not spoken):
• Freestyle zone - ask at least ONE before any scheduling/close
• Slightly more sales-forward tone allowed for custom purposes
• Cap at ~45s, redirect after 10s off‑topic

`;
  }
  
  return `S3 – Curiosity Check

Ask engaging questions about their business needs.

Agent Instructions (not spoken):
• Freestyle zone - ask at least ONE before any scheduling/close
• Cap at ~45s, redirect after 10s off‑topic

`;
}

function buildS4(form: SafeModeFormData, scriptMode?: string): string {
  const purpose = form.safeModePurpose || 'referral';
  let purposeSpecificScheduling = '';
  
  if (purpose === "googlemaps") {
    purposeSpecificScheduling = 'If clear interest (scheduling): "Perfect! Let\'s set up that chat — would Wednesday at 2 or Thursday at 11 work better for you?"';
  } else if (purpose === "referral") {
    purposeSpecificScheduling = 'If clear interest (scheduling): "Perfect! Let\'s set up that chat — would Wednesday at 2 or Thursday at 11 work better for you?"';
  } else if (purpose === "interview") {
    purposeSpecificScheduling = 'If clear interest (scheduling): "Perfect! Let\'s set up that chat — would Wednesday at 2 or Thursday at 11 work better for you?"';
  } else if (purpose === "directory") {
    purposeSpecificScheduling = 'If clear interest (scheduling): "Perfect! Let\'s set up that chat — would Wednesday at 2 or Thursday at 11 work better for you?"';
  } else {
    purposeSpecificScheduling = 'If clear interest (scheduling): "Perfect! Let\'s set up that chat — would Wednesday at 2 or Thursday at 11 work better for you?"';
  }

  let s4Content = `S4 – Schedule or Info

${purposeSpecificScheduling}
Canonical (emit only once time is accepted): schedule_confirmed
Follow-up spoken after acceptance: "Perfect, I'll send a confirmation."

BOOK_NOW LOGIC (when prospect offers a time):
• If prospect proposes ANY time → accept immediately, reflect, confirm, capture email
• Use canonicals: book_now_accept → book_now_email → book_now_confirm
• Example: "Friday at 10 — perfect. I'll lock that in. What's the best email for the invite? Great — you're set for Friday at 10."
• NEVER leave open loop when prospect offers time

If neutral/hesitant (info path): "Totally get it — I can send a short overview by email and then follow up by phone. Does that work?"
If yes → Spoken: "What's the best email for {{contactName}}?"
[PAUSE:300]
"I'll send that over and follow up with a quick call later to make sure it reached you — is that okay?"
[PAUSE:200]

[[INTERNAL: If YES/SURE/OKAY/THAT WORKS/SOUNDS GOOD]]
"Perfect — thanks for the okay to follow up by phone. I'll keep it quick."
[PAUSE:200]
"Appreciate your time today."

[[INTERNAL: If NO/JUST EMAIL/DON'T CALL]]
"No problem — I'll just email the overview. If you want to chat later, you can reply anytime."
[PAUSE:200]
"Thanks again for your time."

On capture → Canonicals:
• consent_email (with address)
• consent_callback_general (only if they say yes to follow-up call)

Agent Instructions (not spoken):
• BOOK_NOW takes priority: When prospect offers time, accept immediately using canonicals
• NO PHONE CONFIRMATION in info path - email capture only
• Offer specific times (day + clock time only)
• If phone_confirmed=true, suppress any "best number" prompts
• MUST ask for explicit callback consent after email capture

`;

  // Add Standard Mode specific objection handling
  if (scriptMode === 'standard') {
    s4Content += `
STANDARD MODE OBJECTION HANDLING:

Pricing Redirect:
If asked price before fit: Use pricing_redirect canonical
"Fair question — pricing depends on area and volume. The 10-min intro covers that. Does Wed at 2 or Thu at 11 work?"

Micro-Rapport (once only):
You may mirror one phrase or acknowledge a distraction briefly, then pivot to booking
Example: "Hope the squirrel's okay. Quick 10 to see if this fits — tomorrow or the day after?"

Agent Instructions (not spoken):
• Pricing questions = immediate redirect to scheduling using canonical
• Allow ONE micro-rapport moment per call maximum
• Always pivot back to booking after any objection handling

`;
  }

  return s4Content;
}

function buildS5(form: SafeModeFormData): string {
  return `S5 – Follow‑up Logistics

For Scheduled Calls:
"Just to confirm — this number works best, or is there another one you prefer?"
"And is there a particular time of day that's generally better for you?"

For Info Requests:
"I'll get that over to you today."
"Just to confirm — any specific areas you're most curious about?"

Agent Instructions (not spoken):
• Redirect to topic within 10s if off‑topic
• Stay focused on confirming logistics only

`;
}

function buildS6(form: SafeModeFormData): string {
  return `S6 – Objection Handling / Close

Social Proof Close:
"I totally understand the hesitation — honestly, most of the [businesses/teams] I talk to feel the same way initially."
"But here's what I've noticed: the ones who take 10 minutes to explore it usually say it was the best decision they made this quarter."

Time Objection:
"I get that you're swamped — that's exactly why this might be perfect timing."
"Quick question: if this could save you [time/headaches/effort] without adding to your plate, would 10 minutes be worth it?"

Email Objection with Explicit Consent:
"Totally. What's the best email for {{contactName}} so I can send a short overview?"
[PAUSE:300]
"I'll send that over and follow up with a quick call later to make sure it reached you — is that okay?"
[PAUSE:200]

[[INTERNAL: If YES/SURE/OKAY/THAT WORKS/SOUNDS GOOD]]
"Perfect — thanks for the okay to follow up by phone. I'll keep it quick."
[PAUSE:200]
"Appreciate your time today."

[[INTERNAL: If NO/JUST EMAIL/DON'T CALL]]
"No problem — I'll just email the overview. If you want to chat later, you can reply anytime."
[PAUSE:200]
"Thanks again for your time."

Soft Close:
"Look, I'm not trying to pressure you into anything."
"But I'd hate for you to miss out on something that could genuinely help your business."
"Can we just hop on for 10 minutes so you can see if it's a fit? If not, no harm done."

Final Attempt:
"Anything else on your mind before we wrap?"

If silence: "I'll let you go — okay if I call back?" (consent_callback_general)

Agent Instructions (not spoken):
• Let agent close naturally with appropriate purpose-specific wrap-up
• Do not read hardcoded closing lines unless purpose-specific
• ALWAYS ask for explicit callback consent when offering email follow-up

`;
}

function buildCanonicalsBlock(): string {
  return `Canonical Confirmation Lines (speak exactly one per outcome)

Schedule Confirmed: ${CANONICALS.schedule_confirmed}
Consent Email: ${CANONICALS.consent_email}
Ask Name: ${CANONICALS.ask_name}
Ask Name Confirmed: ${CANONICALS.ask_name_confirmed}
Ask Gatekeeper Name: ${CANONICALS.ask_gatekeeper_name}
Book Now Accept: ${CANONICALS.book_now_accept}
Book Now Email: ${CANONICALS.book_now_email}
Book Now Confirm: ${CANONICALS.book_now_confirm}
Reschedule Confirmed: ${CANONICALS.rescheduleConfirmed}
Callback General: ${CANONICALS.consent_callback_general}
Do Not Call: ${CANONICALS.do_not_call}
Wrong Number: ${CANONICALS.wrong_number}
Info Email Requested: ${CANONICALS.info_email_requested}
Gatekeeper Schedule: ${CANONICALS.gatekeeper_schedule}
No Action: ${CANONICALS.no_action}
Pricing Redirect: ${CANONICALS.pricing_redirect}
Voicemail Standard: ${CANONICALS.voicemail_standard}

`;
}

function buildSystemRules(form: SafeModeFormData, scriptMode?: string): string {
  let rules = `System Rules (not spoken)

GLOBAL SAFE MODE RULES (Non-Negotiable):
• Identity: You are ${form.userName || MERGE_DEFAULTS.userName} from ${form.companyName || MERGE_DEFAULTS.companyName}. Do not say you are AI.
• Always conversational, friendly, and natural — no robotic phrasing
• Avoid rigid scripts unless required by S1/S4 canonicals
• Never reference AI, automation, or that the call is scripted
• No time-of-day greetings ("good morning," "good evening") — works in all time zones
• Do not speak numeric phone numbers aloud. If phone number is requested - Agent is to say they can be reached at the number on their caller id
• Do not speak email addresses aloud (confirm them verbally, but not spell them in audio unless requested)

NAME CAPTURE LOGIC:
• MANDATORY: If contact confirms they handle it → immediately ask name using ask_name_confirmed canonical
• If gatekeeper says someone else handles it → immediately ask using ask_gatekeeper_name canonical
• DO NOT offer times until a relevant name is known
• Use name after capture: "Thanks, [Name]."

BOOK_NOW LOGIC:
• When prospect offers ANY time → accept immediately using book_now_accept canonical
• Follow with book_now_email canonical: "What's the best email for the invite?"
• End with book_now_confirm canonical: "Great — you're set for [Day] at [Time]."
• NEVER leave open loop when prospect proposes time

PAUSE TOKEN MAPPING:
• [PAUSE] → <break time="700ms"/> (SSML or engine equivalent)
• Store [PAUSE] in text; convert to SSML <break time="700ms"/> at send
• Ensure TTS receives SSML format, not raw text
• CRITICAL: No "Break time equals" artifacts in audio output

VOICEMAIL MODE (Universal - applies to ALL scripts):
• On voicemail/beep detection → switch to 15-20 second voicemail and end
• Use voicemail_standard canonical with merge tags
• End cleanly after voicemail message, no further interaction

GUARDRAILS (Stop Regressions):
• phone_confirmed flag: If true, suppress any "best number" prompts in same call
• Single-confirm debounce: Suppress identical confirmation utterances within 30s
• No double phone ask in same call when number is known/confirmed

CONVERSATION FLOW:
• Always include at least one S3 curiosity probe before scheduling or sending info
• Always request email in S4 info path (capture silently, no repeat aloud)
• Never read aloud any email addresses or phone numbers (including caller ID)
• No time‑of‑day phrases. Offer day + clock time only
• Do not assume the prospect's name; only use it if given or clearly present in data
• If prospect asks for phone: "The number on your caller ID is our main line."
• Redirect off‑topic after ~10s; if still stuck or ~2:45 total, use fallback once
• Keep total call < 3 minutes (hard cap 4)
• No service pitching; act as a helpful consultant gathering facts and offering next steps
• Let agent close naturally unless a wrap-up is purpose-specific
• CRITICAL: Never read aloud any content marked "Agent Instructions (not spoken)" or parenthetical instructions
• ALWAYS ask for explicit callback consent when collecting email addresses

`;

  // Add Standard Mode specific rules
  if (scriptMode === 'standard') {
    rules += `
STANDARD MODE SPECIFIC RULES:
• S2 Hook MUST be ≤20 words maximum, one sentence only
• Short explainer rule: If asked "what is it?" → ≤10 seconds then pivot to booking
• Pricing questions = immediate redirect using pricing_redirect canonical
• Micro-rapport allowed once only per call, then pivot to booking
• Voicemail mode applies: 15-20 seconds maximum, then end cleanly

`;
  }

  return rules;
}

function buildCompanyDetailsReference(form: SafeModeFormData): string {
  return `Company Details Reference (not spoken)

User Details:
• Name: ${form.userName || MERGE_DEFAULTS.userName}
• Company: ${form.companyName || MERGE_DEFAULTS.companyName}
• Title: Representative
• Phone: [Use caller ID number]

Company Information:
• Industry Focus: ${form.industry || form.service || MERGE_DEFAULTS.industry}
• Service Area: ${form.area || MERGE_DEFAULTS.area}
• Contact Method: ${form.channel || MERGE_DEFAULTS.channel}

`;
}

export function generateSafeModeScript(form: SafeModeFormData, scriptMode?: string): string {
  const cleanScript = `"[PAUSE:700] Hey, this is {{userName}} with {{companyName}} — I'm looking to get some details on your {{industry}} work. Are you usually the person who handles new project inquiries and referrals, or is there someone else on your team?"

Gatekeeper Flow

"You must keep things running smoothly there." [PAUSE]
"Sounds like you're the one holding everything together." [PAUSE]
"Got it." [PAUSE:700] "Would it be possible to set up a quick 10-minute chat with {{dmName}}? I'm available {{optionOne}} or {{optionTwo}}."

Hook (pick one)

"Short version: helps {{industry}} teams get referrals faster in {{serviceArea}} — worth a quick chat?"
"We plug you into a pre-ranked site so calls start sooner — does that sound useful?"
"Simple path to more inbound referrals without new ad spend — worth exploring?"

Curiosity

"What types of work do you typically refer out vs. handle in-house?"
"How do you usually handle it when you're swamped or have overflow?"
"Any particular projects you'd want to be considered for?"

Schedule

"Perfect! Let's set up that chat — would {{schedOptionOne}} or {{schedOptionTwo}} work better for you?"
"Friday at 10 — perfect. I'll lock that in. What's the best email for the invite?"
"Great — you're set for Friday at 10."

Info Path

"Totally get it — I can send a short overview by email and then follow up by phone. Does that work?"
"What's the best email for {{contactName}}?"
"I'll send that over and follow up with a quick call later to make sure it reached you — is that okay?"
"Perfect — thanks for the okay to follow up by phone. I'll keep it quick."
"No problem — I'll just email the overview. If you want to chat later, you can reply anytime."
"Thanks again for your time."

Follow-Up Logistics

"For the calendar — does this number work best, or is there another one you prefer?"
"Is there a time of day that's generally better for you?"
"I'll get that over to you today."
"Any specific areas you're most curious about?"

Objections / Close

"I totally understand — most {{industry}} teams feel the same way initially."
"The ones who take 10 minutes usually say it was worth it."
"I get that you're swamped — that's why this might be perfect timing."
"If this could save headaches without adding to your plate, would 10 minutes be worth it?"
"Look, I'm not trying to pressure you into anything."
"I'd hate for you to miss something that could genuinely help."
"Can we hop on for 10 minutes so you can see if it's a fit? If not, no harm done."
"Anything else on your mind before we wrap?"
"I'll let you go — okay if I call back?"

Canonicals (speak verbatim when relevant)

"Perfect! I'll send a confirmation."
"Great! I'll email that over."
"Sorry, I didn't catch your name."
"Great — and I didn't catch your name?"
"Got it — and what's their name?"
"[Day] at [Time] — perfect. I'll lock that in."
"What's the best email for the invite?"
"Great — you're set for [Day] at [Time]."
"No problem! I'll move things around and get back to you."
"Perfect! I'll try you again later this week."
"Understood! I'll make sure we don't reach out again."
"My apologies! I'll update our records."
"Absolutely! I'll email that information over."
"That would be great! I'll send the details and we can coordinate."
"Thanks for your time!"
"Fair question — pricing depends on area and volume. The 10-min intro covers that. Does Wed at 2 or Thu at 11 work?"

Voicemail

"Hey, it's {{userName}} with {{companyName}}. Quick idea to help with {{purpose}} for your {{industry}} team in {{serviceArea}}. I'll email a 1-pager and try you once more — if you prefer, call or text back at the number on your caller ID. Thanks!"`;

  // Apply merge tags and add missing defaults
  const processedScript = applyMergeTags(cleanScript, form)
    .replace(/\{\{dmName\}\}/g, 'the decision-maker')
    .replace(/\{\{optionOne\}\}/g, 'tomorrow at 2')
    .replace(/\{\{optionTwo\}\}/g, 'the day after at 11')
    .replace(/\{\{schedOptionOne\}\}/g, 'Wednesday at 2')
    .replace(/\{\{schedOptionTwo\}\}/g, 'Thursday at 11')
    .replace(/\{\{contactName\}\}/g, 'you')
    .replace(/\{\{serviceArea\}\}/g, form.area || 'your area')
    .replace(/\{\{purpose\}\}/g, form.safeModePurpose === 'referral' ? 'referral opportunities' : 
                                   form.safeModePurpose === 'interview' ? 'a quick interview' :
                                   form.safeModePurpose === 'directory' ? 'directory listing' :
                                   form.safeModePurpose === 'googlemaps' ? 'Google Maps optimization' :
                                   form.customPurpose || 'business opportunities');

  return processedScript;
}

// Export canonicals for compatibility
export const SafeModeCanonicals = CANONICALS;